import React from 'react';
import { useStore } from 'effector-react';
import { formatSize, toFlag, toPercent } from 'pano-utils';
import { Scrollbars } from 'react-custom-scrollbars';
import { type Audio, LibType, MediaType } from '@types';
import { $sceneMapApi } from '@stores/dataStores';
import { $loadedScene } from '@stores/loadedStore';
import { showApplyWarning } from '@components/ConfirmModal';
import Title from '@components/Title';
import { sendSuccess } from '@components/Noti';
import AudioPlayer from '@components/AudioPlayer';
import Tip from '@components/Tip';
import Button from '@components/Button';
import Slider from '@components/Slider';
import FeatureToggle from '@components/FeatureToggle';
import { Collapse, CollapseWrapper } from '@components/Collapse';
import { LIB_SIZE_LIMIT, selectLibAudios } from '../../mediaLib';
import SidebarApplyTo from '../components/SidebarApplyTo';
import { showSceneModal } from '../../category';
import SidebarHeader from '../components/SidebarHeader';

const sizeLimit = formatSize(LIB_SIZE_LIMIT[LibType.audio]);

const audioDefault: Audio = {
  volume: 1,
  autoPlay: 1,
  isLoop: 1,
};

export function SidebarMusic() {
  const scene = useStore($loadedScene)!;
  const { id, sound } = scene;

  const handleSelectAudio = async () => {
    const { libItems, sysItems } = await selectLibAudios({ defaultNav: 'sys' });
    if (libItems) {
      const item = libItems[0];
      $sceneMapApi.updateScene({
        id,
        updater: (draft) => {
          draft.sound = {
            ...audioDefault,
            ...draft.sound,
            type: MediaType.lib,
            mediaId: item.id,
            mediaUrl: item.url,
            mediaName: item.name,
          };
        },
      });
    } else if (sysItems) {
      const item = sysItems[0];
      $sceneMapApi.updateScene({
        id,
        updater: (draft) => {
          draft.sound = {
            ...audioDefault,
            ...draft.sound,
            type: MediaType.system,
            mediaId: item.id,
            mediaUrl: item.url,
            mediaName: item.name,
          };
        },
      });
    }
  };

  const updateValue = <T extends keyof Audio>({
    key,
    value,
  }: {
    key: T;
    value: Audio[T];
  }) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.sound[key] = value;
      },
    });
  };

  const handleApplyTo = async () => {
    if (!scene.sound?.mediaUrl) {
      const { confirmed } = await showApplyWarning();
      if (!confirmed) return;
    }
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.sound = sound;
      });
    });
    sendSuccess('背景音乐已应用到所选场景');
  };

  return (
    <Scrollbars>
      <CollapseWrapper>
        <SidebarHeader title={<Title>背景音乐</Title>} />
        <Collapse title="音乐设置">
          <AudioPlayer
            mediaType={sound.type}
            src={sound.mediaUrl}
            name={sound.mediaName}
            volume={sound.volume}
            onDelete={() => {
              $sceneMapApi.updateScene({
                id,
                updater: (draft) => {
                  draft.sound = {};
                },
              });
            }}
          />
          <div className="mt-10 flex items-center justify-between">
            <Tip>{`文件体积不超过${sizeLimit}`}</Tip>
            <Button size="sm" onClick={handleSelectAudio}>
              选择音频
            </Button>
          </div>
          {!!sound.mediaUrl && (
            <>
              <Slider
                min={0}
                max={1}
                value={sound.volume}
                className="mt-20"
                valToString={(val) => toPercent(val, 0)}
                onChange={(value) => updateValue({ key: 'volume', value })}
              >
                <Slider.Header
                  title={
                    <div>
                      <span>音量：</span>
                      <Tip as="span">仅PC和安卓有效</Tip>
                    </div>
                  }
                />
                <Slider.Bar step={0.01} />
              </Slider>
              <FeatureToggle
                title="自动播放"
                isOn={!!sound.autoPlay}
                onToggle={(bool) => {
                  updateValue({ key: 'autoPlay', value: toFlag(bool) });
                }}
              />
              <FeatureToggle
                title="循环播放"
                isOn={!!sound.isLoop}
                onToggle={(bool) => {
                  updateValue({ key: 'isLoop', value: toFlag(bool) });
                }}
              />
            </>
          )}
        </Collapse>
        <SidebarApplyTo
          className="mt-10"
          title="把背景音乐应用到..."
          onClick={handleApplyTo}
        />
      </CollapseWrapper>
    </Scrollbars>
  );
}
